import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Loader2, Trophy, TrendingUp, Users, KeyRound, Coins, Radar, X, UserPlus } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { EmailVerifyBanner } from "@/components/EmailVerifyBanner";
import { shareInvite } from "@/lib/invite";

const CAPS = [2, 4, 6];
const BUYINS = [0, 1, 2, 5, 10];
const MM_TIMEOUT_MS = 20000;

export default function Lobby() {
  const { t } = useLang();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [openTables, setOpenTables] = useState([]);
  const [starting, setStarting] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [capacity, setCapacity] = useState(2);
  const [buyIn, setBuyIn] = useState(1);
  const [marketType, setMarketType] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/matches");
      setHistory(data);
    } catch (e) { console.warn("history load failed:", e); }
  };
  const loadOpen = async () => {
    try {
      const { data } = await api.get("/rooms/open");
      setOpenTables(data);
    } catch (e) { console.warn("open tables load failed:", e); }
    try {
      const { data } = await api.get("/presence");
      setPresence(data);
    } catch (e) { console.warn("presence load failed:", e); }
  };
  useEffect(() => {
    load(); loadOpen();
    const iv = setInterval(loadOpen, 3000);
    return () => clearInterval(iv);
  }, []);

  const [mm, setMm] = useState(null); // matchmaking: { waited }
  const [presence, setPresence] = useState(null);
  const mmRef = useRef({ active: false, timer: null, poll: null });

  const stopMm = () => {
    mmRef.current.active = false;
    clearTimeout(mmRef.current.timer);
    clearInterval(mmRef.current.poll);
    setMm(null);
  };

  const startBotMatch = async () => {
    const { data } = await api.post("/matches", { market_type: marketType === "all" ? null : marketType });
    navigate(`/match/${data.id}`);
  };

  const goToMatch = (matchId) => {
    stopMm();
    toast.success(t("mm_found"));
    navigate(`/match/${matchId}`);
  };

  const startMatch = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/matchmaking/join", { market_type: marketType === "all" ? null : marketType });
      if (data.status === "matched") return goToMatch(data.match_id);
      mmRef.current.active = true;
      setMm({ waited: 0 });
      mmRef.current.poll = setInterval(async () => {
        if (!mmRef.current.active) return;
        try {
          const { data: s } = await api.get("/matchmaking/status");
          if (s.status === "matched") goToMatch(s.match_id);
          else if (s.status === "waiting") setMm({ waited: s.waited });
        } catch (e) { console.warn("matchmaking poll failed:", e); }
      }, 1500);
      mmRef.current.timer = setTimeout(async () => {
        if (!mmRef.current.active) return;
        try {
          const { data: l } = await api.post("/matchmaking/leave");
          if (l.status === "matched") return goToMatch(l.match_id);
          stopMm();
          toast(t("mm_fallback"));
          await startBotMatch();
        } catch (err) {
          stopMm();
          toast.error(formatApiError(err.response?.data?.detail) || err.message);
          setStarting(false);
        }
      }, MM_TIMEOUT_MS);
    } catch (err) {
      stopMm();
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setStarting(false);
    }
  };

  const cancelMm = async () => {
    stopMm();
    setStarting(false);
    try { await api.post("/matchmaking/leave"); } catch (e) { console.warn("leave queue failed:", e); }
  };

  useEffect(() => () => {
    const r = mmRef.current;
    if (r.active) {
      r.active = false; clearTimeout(r.timer); clearInterval(r.poll);
      api.post("/matchmaking/leave").catch((e) => console.warn("leave queue failed:", e));
    }
  }, []);

  const createRoom = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", { capacity, buy_in: buyIn });
      refreshUser();
      navigate(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const challengeFriend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", { capacity: 2, buy_in: 0 });
      const res = await shareInvite(data.code, t("invite_text"));
      if (res === "copied") toast.success(t("invite_copied"));
      navigate(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const joinById = async (rid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${rid}/join-id`);
      refreshUser();
      navigate(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const oldCreateRoom = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rooms");
      navigate(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const joinRoom = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/rooms/join", { code: code.trim().toUpperCase() });
      navigate(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-base text-white">
      <Navbar />
      <EmailVerifyBanner />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 overflow-x-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* quick match */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-7 min-w-0 relative overflow-hidden border border-white/10 bg-panel p-6 md:p-10"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(234,179,8,0.1),transparent_60%)]" />
            <div className="relative">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">{t("quick_match")}</span>
              <h1 className="mt-4 font-heading font-black text-4xl md:text-5xl tracking-tight leading-none">
                It's a match.<br />Not a game.
              </h1>
              <p className="mt-6 text-white/60 max-w-md">{t("lobby_sub")}</p>
              <div className="mt-6" data-testid="market-type-selector">
                <span className="font-mono text-xs uppercase tracking-widest text-white/40">{t("market_type_label")}</span>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {["all", "stock", "forex", "commodity"].map((mt) => (
                    <button
                      key={mt}
                      data-testid={`market-type-${mt}`}
                      onClick={() => setMarketType(mt)}
                      className={`px-3 py-1.5 font-mono text-xs uppercase tracking-wide rounded-sm border transition-colors ${
                        marketType === mt
                          ? "bg-signal text-black border-signal font-bold"
                          : "border-white/15 text-white/60 hover:text-white hover:border-white/30"
                      }`}
                    >
                      {t(`market_${mt}`)}
                    </button>
                  ))}
                </div>
              </div>
              {mm ? (
                <div data-testid="mm-searching" className="mt-8 border border-signal/40 bg-signal/5 p-4 flex items-center gap-4 max-w-md">
                  <Radar className="w-6 h-6 text-signal animate-pulse shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold">{t("mm_searching")}</div>
                    <div className="font-mono text-xs text-white/50 mt-0.5">
                      {t("mm_hint")} · <span data-testid="mm-waited" className="text-signal tabular-nums">{Math.max(0, Math.round(MM_TIMEOUT_MS / 1000 - mm.waited))}s</span>
                    </div>
                  </div>
                  <Button data-testid="mm-cancel-btn" onClick={cancelMm} variant="ghost" size="icon" className="text-white/60 hover:bg-white/10 shrink-0">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="new-match-btn"
                  onClick={startMatch}
                  disabled={starting}
                  className="mt-8 bg-signal text-black hover:bg-white rounded-sm font-semibold h-12 px-8 text-base"
                >
                  {starting ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("starting")}</> : <><Play className="w-5 h-5" /> {t("quick_match")}</>}
                </Button>
              )}
              <div className="mt-4">
                <Button data-testid="challenge-friend-btn" onClick={challengeFriend} disabled={busy || !!mm}
                  className="bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-sm font-semibold h-10 px-5">
                  <UserPlus className="w-4 h-4" /> {t("challenge_friend")}
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-white/40">
                {presence && (
                  <span data-testid="online-counter" className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${presence.online > 1 ? "bg-profit animate-pulse" : "bg-white/30"}`} />
                    <span className="text-white/70 tabular-nums">{presence.online}</span> {t("online_players")}
                    <span className="text-white/20">·</span>
                    <span className={`tabular-nums ${presence.searching > 0 ? "text-signal" : "text-white/70"}`}>{presence.searching}</span> {t("searching_players")}
                  </span>
                )}
                <span>{t("mm_note")}</span>
              </div>
            </div>
          </motion.div>

          {/* cash games */}
          <div className="lg:col-span-5 min-w-0 border border-white/10 bg-panel p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-signal">
                <Coins className="w-5 h-5" />
                <span className="font-mono text-xs uppercase tracking-[0.3em]">{t("cash_games")}</span>
              </div>
              <div className="mt-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">{t("capacity")}</div>
                <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                  {CAPS.map((c) => (
                    <button key={c} data-testid={`cap-${c}`} onClick={() => setCapacity(c)}
                      className={`py-2.5 font-mono font-bold text-sm transition-colors ${capacity === c ? "bg-signal text-black" : "bg-panel hover:bg-panelhover text-white/70"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">{t("buy_in")}</div>
                <div className="grid grid-cols-5 gap-px bg-white/10 border border-white/10">
                  {BUYINS.map((b) => (
                    <button key={b} data-testid={`buyin-${b}`} onClick={() => setBuyIn(b)}
                      className={`py-2.5 font-mono font-bold text-xs sm:text-sm transition-colors ${buyIn === b ? "bg-signal text-black" : "bg-panel hover:bg-panelhover text-white/70"}`}>
                      {b === 0 ? t("free") : `€${b}`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-3 font-mono text-xs text-white/40" data-testid="pot-preview">
                {buyIn === 0
                  ? t("free_table_note")
                  : <>{t("pot")}: <span className="text-profit">€{(capacity * buyIn * 0.8).toFixed(2)}</span> · {t("commission_note")}</>}
              </div>
            </div>
            <div className="mt-5 space-y-3">
              <Button data-testid="create-room-btn" onClick={createRoom} disabled={busy}
                className="w-full bg-signal text-black hover:bg-white rounded-sm font-semibold h-11">
                <Coins className="w-4 h-4" /> {t("create_table")} · {buyIn === 0 ? t("free") : `€${buyIn}`}
              </Button>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input data-testid="room-code-input" value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("enter_code")}
                    className="pl-9 bg-panelhover border-white/10 text-white font-mono uppercase tracking-widest rounded-sm h-11" />
                </div>
                <Button data-testid="join-room-btn" onClick={joinRoom} disabled={busy || !code.trim()}
                  className="bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-sm h-11 px-5">
                  {t("join_btn")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* open tables */}
        <div className="mt-10">
          <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
            <Coins className="w-5 h-5 text-signal" /> {t("open_tables")}
          </h2>
          {openTables.length === 0 ? (
            <div className="border border-white/10 bg-panel p-8 text-center text-white/40 font-mono text-sm">{t("no_tables")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {openTables.map((r) => (
                <div key={r.id} data-testid={`table-${r.id}`} className="border border-white/10 bg-panel p-5 hover:bg-panelhover transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-2xl text-signal">{r.buy_in === 0 ? t("free") : `€${r.buy_in}`}</span>
                    <span className="font-mono text-xs text-white/40">{r.players}/{r.capacity} · {r.seats_left} {t("seats_left")}</span>
                  </div>
                  <div className="mt-2 font-mono text-xs text-white/50">{t("pot")}: <span className="text-profit">€{r.pot.toFixed(2)}</span></div>
                  <Button data-testid={`join-table-${r.id}`} onClick={() => joinById(r.id)} disabled={busy}
                    className="mt-4 w-full bg-signal text-black hover:bg-white rounded-sm font-semibold h-10">
                    {t("join_table")} · {r.buy_in === 0 ? t("free") : `€${r.buy_in}`}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* history */}
        <div className="mt-14">
          <h2 className="font-heading font-bold text-xl mb-6 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-signal" /> {t("history")}
          </h2>
          {history.length === 0 ? (
            <div className="border border-white/10 bg-panel p-10 text-center text-white/40 font-mono text-sm">
              {t("no_history")}
            </div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10">
              {history.map((m) => {
                const finished = m.status === "finished";
                const mr = m.my_result;
                return (
                  <div
                    key={m.id}
                    data-testid={`history-row-${m.id}`}
                    onClick={() => navigate(finished ? `/results/${m.id}` : `/match/${m.id}`)}
                    className="flex items-center justify-between p-4 bg-panel hover:bg-panelhover transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <TrendingUp className={`w-5 h-5 ${mr?.won ? "text-profit" : "text-white/30"}`} />
                      <div>
                        <div className="font-mono text-sm flex items-center gap-2">
                          {m.instruments.map((i) => i.symbol).join(" · ")}
                          {m.mode === "multiplayer" && <Users className="w-3.5 h-3.5 text-signal" />}
                        </div>
                        <div className="font-mono text-xs text-white/40">{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      {finished && mr ? (
                        <>
                          <div className={`font-mono font-bold tabular-nums ${mr.profit >= 0 ? "text-profit" : "text-loss"}`}>
                            {mr.profit >= 0 ? "+" : ""}{mr.profit.toLocaleString()}
                          </div>
                          <div className="font-mono text-xs text-white/40">{t("rank")} #{mr.rank}</div>
                        </>
                      ) : (
                        <span className="font-mono text-xs text-signal uppercase">{t("match_active")}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
